import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useBreakpoint } from '@ocean/hooks'
import { Mode } from '@ocean/types/dist/appearance'
import { modals } from '@ocean/ui'
import { useLazySelector } from 'hooks'
import { redirectToSta } from 'core/staIntegration/slices/staIntegration'
import { StaRedirectParams } from 'core/staIntegration/types'
import { HighlightedNotifications } from 'modules/Notifications/components'
import { namespace } from 'modules/Notifications/localization'
import {
  loadUserHighlightedAndEventNotificationsData,
  markNotificationAsRead,
  setHighlightedViewedState,
} from 'modules/Notifications/slices/notifications'
import { HighlightedNotificationsUtils } from 'modules/Notifications/utils'

const { Popup } = modals

const I18_PATH = `${namespace}:notifications.highlighted`

const HighlightedNotificationsContainer: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isDesktop = useBreakpoint('sm')
  const { highlightedNotifications, highlightedViewed, onboardingViewed, companyNoticeAlias } =
    useLazySelector(({ notifications, onboarding, environment }) => {
      const { viewed: onboardingViewed } = onboarding
      const { highlightedNotifications, highlightedViewed } = notifications
      const { configuration } = environment

      return {
        highlightedNotifications,
        highlightedViewed,
        onboardingViewed,
        companyNoticeAlias: configuration.result?.configuration.companyNoticeAlias,
      }
    })
  const storageHighlightedNotificationsViewed =
    HighlightedNotificationsUtils.getHighlightedNotificationsState() ===
    HighlightedNotificationsUtils.viewed
  const highlightedNotificationsViewed = highlightedViewed || storageHighlightedNotificationsViewed

  useEffect(() => {
    if (!highlightedNotifications) {
      dispatch(loadUserHighlightedAndEventNotificationsData())
    }
    dispatch(setHighlightedViewedState(highlightedNotificationsViewed))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function handleClose() {
    HighlightedNotificationsUtils.markHighlightedNotificationsViewed()
    dispatch(setHighlightedViewedState(true))
  }

  function handleRedirectToSta(redirectParams?: StaRedirectParams) {
    dispatch(redirectToSta(redirectParams))
  }

  function handleMarkAsRead(id: Guid) {
    dispatch(markNotificationAsRead(id))
  }

  if (!highlightedNotifications?.length) return null

  return (
    <Popup
      isOpen={!highlightedViewed && onboardingViewed}
      onClose={handleClose}
      title={t(`${I18_PATH}.title`)}
      mode={Mode.Dark}
      width={isDesktop ? '728px' : undefined}
      dataTest='HighlightedNotificationsPopup'
    >
      <HighlightedNotifications
        notifications={highlightedNotifications}
        companyNoticeAlias={companyNoticeAlias}
        onRedirect={handleRedirectToSta}
        onMarkAsRead={handleMarkAsRead}
        onClose={handleClose}
      />
    </Popup>
  )
}

export default HighlightedNotificationsContainer
